import { useCallback, useState } from 'react';
import { getLabel, getPreviousIndications } from '../../../api/submission_details';
import { HttpResponse } from '../constant';
import type { PreviousApplication } from '../types';

interface UseComparePdfsParams {
  applno: string;
  submissionType: string;
  submissionNumber: string;
  center: string;
  documentId: string | undefined;
  section?: string | null;
}

interface ComparePdfsState {
  open: boolean;
  previousApplication: PreviousApplication | null;
  previousPdfUrl: string;
  previousPdfLoading: boolean;
  previousPdfError: string;
  currentComparePdfUrl: string;
  currentComparePdfLoading: boolean;
  currentComparePdfError: string;
  isLoadingPdfModal: boolean;
}

type PdfTarget = 'current' | 'previous';

const LABEL_ERROR_MESSAGES: Record<PdfTarget, string> = {
  current: 'Could not load current submission label.',
  previous: 'Could not load previous submission label.'
};

interface FetchLabelPdfParams {
  applno: string;
  submissionType: string;
  submissionNumber: string;
  center: string;
  docId: number;
  sectionParam: string | undefined;
}

export default function useComparePdfs({
  applno,
  submissionType,
  submissionNumber,
  center,
  documentId,
  section
}: UseComparePdfsParams) {
  const [state, setState] = useState<ComparePdfsState>({
    open: false,
    previousApplication: null,
    previousPdfUrl: '',
    previousPdfLoading: false,
    previousPdfError: '',
    currentComparePdfUrl: '',
    currentComparePdfLoading: false,
    currentComparePdfError: '',
    isLoadingPdfModal: false
  });

  const updateState = (updates: Partial<ComparePdfsState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const fetchLabelPdf = useCallback(async (params: FetchLabelPdfParams, target: PdfTarget) => {
    try {
      const response = await getLabel(
        params.applno,
        params.submissionType,
        Number(params.submissionNumber),
        params.center,
        params.docId,
        params.sectionParam
      );
      if (response?.data?.Status === HttpResponse.OK && response?.data?.Success?.pdf_link) {
        const url = response.data.Success.pdf_link;
        updateState(target === 'current' ? { currentComparePdfUrl: url } : { previousPdfUrl: url });
      } else {
        updateState(
          target === 'current'
            ? { currentComparePdfError: LABEL_ERROR_MESSAGES.current }
            : { previousPdfError: LABEL_ERROR_MESSAGES.previous }
        );
      }
    } catch (e) {
      console.error(`Failed to load ${target} label PDF:`, e);
      updateState(
        target === 'current'
          ? { currentComparePdfError: LABEL_ERROR_MESSAGES.current }
          : { previousPdfError: LABEL_ERROR_MESSAGES.previous }
      );
    } finally {
      updateState(
        target === 'current' ? { currentComparePdfLoading: false } : { previousPdfLoading: false }
      );
    }
  }, []);

  const runPdfFetches = useCallback(
    (sectionParam: string | undefined, docId: number, previousApp: PreviousApplication | null) => {
      updateState({
        previousPdfError: '',
        currentComparePdfError: '',
        currentComparePdfUrl: '',
        previousPdfUrl: '',
        currentComparePdfLoading: true,
        previousPdfLoading: true
      });

      const currentParams: FetchLabelPdfParams = {
        applno,
        submissionType,
        submissionNumber,
        center,
        docId,
        sectionParam
      };
      fetchLabelPdf(currentParams, 'current');

      if (!previousApp) {
        updateState({ previousPdfLoading: false });
        return;
      }
      fetchLabelPdf(
        {
          applno: previousApp.applno,
          submissionType: previousApp.submission_type,
          submissionNumber: String(previousApp.submission_number),
          center,
          docId,
          sectionParam
        },
        'previous'
      );
    },
    [applno, submissionType, submissionNumber, center, fetchLabelPdf]
  );

  const openComparePdfs = useCallback(() => {
    const docId = documentId != null ? Number(documentId) : 0;
    const sectionParam = section || undefined;

    updateState({
      open: true,
      previousApplication: null,
      previousPdfUrl: '',
      previousPdfError: '',
      currentComparePdfUrl: '',
      currentComparePdfError: '',
      isLoadingPdfModal: true,
      currentComparePdfLoading: true,
      previousPdfLoading: true
    });

    const currentParams: FetchLabelPdfParams = {
      applno,
      submissionType,
      submissionNumber,
      center,
      docId,
      sectionParam
    };
    fetchLabelPdf(currentParams, 'current');

    const fetchPreviousData = async () => {
      let prevApp: PreviousApplication | null = null;
      try {
        const prevResponse = await getPreviousIndications(
          applno,
          submissionType,
          submissionNumber,
          center
        );
        const success = prevResponse?.data?.Success ?? prevResponse?.Success;
        if (success?.previous_application) {
          prevApp = success.previous_application;
          updateState({ previousApplication: prevApp });
        }
      } catch (e) {
        console.error('Failed to fetch previous application for PDF compare:', e);
        updateState({ previousApplication: null });
      } finally {
        updateState({ isLoadingPdfModal: false });
      }

      if (!prevApp) {
        updateState({ previousPdfLoading: false });
        return;
      }

      await fetchLabelPdf(
        {
          applno: prevApp.applno,
          submissionType: prevApp.submission_type,
          submissionNumber: String(prevApp.submission_number),
          center,
          docId,
          sectionParam
        },
        'previous'
      );
    };

    fetchPreviousData();
  }, [applno, submissionType, submissionNumber, center, documentId, section, fetchLabelPdf]);

  const loadPdfsForTab = useCallback(
    (sectionParam: string | undefined, documentIdParam: string | undefined) => {
      const docId = documentIdParam != null ? Number(documentIdParam) : 0;
      runPdfFetches(sectionParam, docId, state.previousApplication);
    },
    [runPdfFetches, state.previousApplication]
  );

  const closeComparePdfs = () => updateState({ open: false });

  return {
    ...state,
    openComparePdfs,
    loadPdfsForTab,
    closeComparePdfs
  };
}
